<template>
	<HeroLayout v-model="showContactUs">
		<div class="flex flex-col w-full h-full">
			<img class="registration-title-image" :src="logoPath" alt="Logo" />

			<div class="flex flex-col w-fit p-9 border border-neutral rounded-xl card-shadow">
				<form>
					<p class="registration-title text-center">Verify Your Account</p>
					<p class="registration-subtitle mb-5 text-center">Enter the 6-digit code sent to {{ tfaStore.maskedEmail }}</p>

					<TwoFactorCodeInput v-model="tfaCode" @update:modelValue="onTfaUpdated" :disabled="isLoading"
											  :update-immediately="false" :length="tfaCodeLength" :error="error !== null"
											  class="w-fit mx-auto" />

					<span v-if="error" class="mt-5 block text-error font-semibold text-center">The verification code entered is incorrect. Please try again.</span>

					<span class="mt-5 block text-grayscale-body w-full text-center" @click="resendTfaCode">Did not receive a code? <span class="clickable-text">Resend</span>.</span>

					<SubmitButton class="ml-auto w-full mt-5" label="Verify" :loading="isLoading" @click="onTfaUpdated" />

					<span
						class="mt-5 block text-label font-semibold leading-6 text-center">Don’t share the verification code with anyone including support staff.</span>
				</form>

			</div>
		</div>
	</HeroLayout>
</template>

<script setup lang="js">
import router from '@/router/router.js'
import { useTwoFactorVerificationStore } from '@/stores/twoFactorVerificationStore.js'
import { onMounted, ref } from 'vue'
import TwoFactorCodeInput from '@/components/Auth/TwoFactorCodeInput.vue'
import { useAuthStore } from '@/stores/authStore.js'
import HeroLayout from '@/layouts/HeroLayout.vue'
import { userApi } from '@/api/User/UserApi.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Tenants } from '@/models/tenants/Tenants.js'
import { Toaster } from '@/models/utils/Toaster.js';

const tfaCodeLength = 6
const tfaStore = useTwoFactorVerificationStore()
const authStore = useAuthStore()
const isLoading = ref(false)
const error = ref(null)
const tfaCode = ref('')
const showContactUs = ref(false)
const toaster = new Toaster();
const logoPath = ref('')
const tenant = ref(null)

onMounted(() =>
{
	if (!tfaStore.email || !tfaStore.password )//|| !tfaStore.tfa_token
	{
		router.push({ name: 'auth.login' })
	}

	tenant.value = Tenants.getTenant()
	logoPath.value = new URL(`../../assets/images/theme/${tenant.value.registrationLogo.image}`, import.meta.url).href
})

function onTfaUpdated()
{
	if (tfaCode.value !== null && tfaCode.value.length === tfaCodeLength)
	{
		submitTfaCode()
	}
}

function submitTfaCode()
{
	isLoading.value = true

	userApi.verifyTfa({
		'user': {
			'email': tfaStore.email,
			'password': tfaStore.password,
			'tfa_token': tfaStore.tfa_token,
			'tfa_code': tfaCode.value,
			'tenant_id': tenant.value.id
		}
	}, (response) => {
		authStore.handleLoginResult(response, tfaStore.email ?? '', tfaStore.password ?? '')

		const user = authStore.getAuth?.user()

		if (!user?.given_name || !user?.family_name || !user?.phone_number) {
			router.push({ name: 'auth.add-profile-info' })//'auth.before-we-continue'
		}
		else
		{
			authStore.userpilotIdentify()
			router.push({ name: 'auth.login-success' })
		}
	}, (e) => {
		isLoading.value = false
		error.value = e.showErrorBanner()
	})
}

function resendTfaCode()
{
	isLoading.value = true
	const tenant = Tenants.getTenant()

	userApi.login(tfaStore.email, tfaStore.password, tenant.id,
		() => {
			isLoading.value = false

			toaster.success('Verification code successfully sent.')

		}, (e) =>
		{
			isLoading.value = false
			error.value = e.showErrorBanner()
		})
}

</script>

<style scoped>

.card-shadow {
	box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.20);
}

</style>